import { supabase } from '@/lib/supabase';

export interface Notification {
  id: string;
  user_id: string;
  order_id: string | null;
  type: 'order_status_change' | 'order_approved' | 'order_rejected' | 'order_shipped' | 'estimate_sent' | 'system';
  title: string;
  message: string;
  is_read: boolean;
  created_at: string;
  read_at: string | null;
  metadata: Record<string, any>;
}

export interface NotificationCounts {
  total: number;
  unread: number;
}

/**
 * Service for managing user notifications
 */
export const notificationService = {
  /**
   * Get all notifications for current user
   */
  getNotifications: async (limit = 50): Promise<{ data: Notification[] | null; error: Error | null }> => {
    try {
      const { data, error } = await supabase
        .from('notifications')
        .select('*')
        .order('created_at', { ascending: false })
        .limit(limit);

      if (error) {
        console.error('Error fetching notifications:', error);
        return { data: null, error: new Error(error.message) };
      }

      return { data: data as Notification[], error: null };
    } catch (error: any) {
      console.error('Error in getNotifications:', error);
      return { data: null, error };
    }
  },

  /**
   * Get unread notifications count
   */
  getUnreadCount: async (): Promise<{ count: number; error: Error | null }> => {
    try {
      const { data: session } = await supabase.auth.getSession();
      
      if (!session?.session?.user?.id) {
        return { count: 0, error: null };
      }

      const { data, error } = await supabase.rpc('get_unread_notification_count', {
        user_uuid: session.session.user.id
      });

      if (error) {
        console.error('Error fetching unread count:', error);
        return { count: 0, error: new Error(error.message) };
      }

      return { count: data || 0, error: null };
    } catch (error: any) {
      console.error('Error in getUnreadCount:', error);
      return { count: 0, error };
    }
  },

  /**
   * Mark notification as read
   */
  markAsRead: async (notificationId: string): Promise<{ success: boolean; error: Error | null }> => {
    try {
      const { data, error } = await supabase.rpc('mark_notification_read', {
        notification_uuid: notificationId
      });

      if (error) {
        console.error('Error marking notification as read:', error);
        return { success: false, error: new Error(error.message) };
      }

      return { success: data === true, error: null };
    } catch (error: any) {
      console.error('Error in markAsRead:', error);
      return { success: false, error };
    }
  },

  /**
   * Mark all notifications as read
   */
  markAllAsRead: async (): Promise<{ count: number; error: Error | null }> => {
    try {
      const { data, error } = await supabase.rpc('mark_all_notifications_read');

      if (error) {
        console.error('Error marking all as read:', error);
        return { count: 0, error: new Error(error.message) };
      }

      return { count: data || 0, error: null };
    } catch (error: any) {
      console.error('Error in markAllAsRead:', error);
      return { count: 0, error };
    }
  },

  /**
   * Subscribe to real-time notification changes
   */
  subscribeToNotifications: (
    userId: string,
    onNotification: (notification: Notification) => void,
    onError?: (error: Error) => void
  ) => {
    const channel = supabase
      .channel('notifications_changes')
      .on(
        'postgres_changes',
        {
          event: 'INSERT',
          schema: 'public',
          table: 'notifications',
          filter: `user_id=eq.${userId}`
        },
        (payload) => {
          if (payload.new) {
            onNotification(payload.new as Notification);
          }
        }
      )
      .subscribe();

    // Return unsubscribe function
    return () => {
      supabase.removeChannel(channel);
    };
  },

  /**
   * Delete old read notifications
   */
  cleanupOldNotifications: async (): Promise<{ count: number; error: Error | null }> => {
    try {
      const { data, error } = await supabase.rpc('cleanup_old_notifications');

      if (error) {
        console.error('Error cleaning up notifications:', error);
        return { count: 0, error: new Error(error.message) };
      }

      return { count: data || 0, error: null };
    } catch (error: any) {
      console.error('Error in cleanupOldNotifications:', error);
      return { count: 0, error };
    }
  }
};